import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  Fade,
  IconButton,
  Tooltip,
  Stack,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CloudUpload as UploadIcon,
  Language as LanguageIcon,
  GitHub as GitHubIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../App';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageMeta';
import { tokens } from '../theme/tokens';

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

// Helper function to convert HTTP URL to WebSocket URL
const getWebSocketUrl = (baseUrl: string) => {
  return baseUrl.replace(/^http/, 'ws')
}

// ─── Tab group definitions ─────────────────────────────────────────────────

const TAB_GROUPS = [
  {
    label: 'Getting Started',
    tabs: [
      { label: 'Quick Start' },
      { label: 'Authentication' },
    ],
  },
  {
    label: 'ASR',
    tabs: [
      { label: 'cURL' },
      { label: 'Node.js' },
      { label: 'Python' },
      { label: 'WebSocket' },
    ],
  },
  {
    label: 'TTS',
    tabs: [
      { label: 'Quick Start' },
      { label: 'REST API' },
      { label: 'WebSocket' },
    ],
  },
] as const;

// ─── CodeBlock ─────────────────────────────────────────────────────────────

const CodeBlock: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const codeBg = isDark ? '#0f172a' : '#f8fafc';
  const codeText = isDark ? '#e2e8f0' : '#1e293b';
  const codeBorder = isDark ? tokens.colors.neutral[700] : tokens.colors.neutral[200];

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        backgroundColor: codeBg,
        color: codeText,
        p: 3,
        borderRadius: `${tokens.radius.md}px`,
        border: `1px solid ${codeBorder}`,
        fontFamily: tokens.typography.fontFamilyMono,
        fontSize: '0.875rem',
        lineHeight: 1.6,
        overflow: 'auto',
        maxHeight: '500px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Chip
          label={language}
          size="small"
          sx={{
            backgroundColor: tokens.colors.primary.main,
            color: isDark ? '#fff' : tokens.colors.neutral[800],
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: `${tokens.radius.pill}px`,
          }}
        />
        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
          <IconButton
            onClick={handleCopy}
            size="small"
            aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
            sx={{
              color: isDark ? tokens.colors.neutral[400] : tokens.colors.neutral[600],
              backgroundColor: copied
                ? (isDark ? 'rgba(16,185,129,0.15)' : tokens.colors.success.light)
                : 'transparent',
              borderRadius: `${tokens.radius.pill}px`,
              px: 1.5,
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(0,207,243,0.12)'
                  : `rgba(0,207,243,0.08)`,
                color: isDark ? tokens.colors.primary.main : tokens.colors.primary.darkText,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: codeText }}>
        <code style={{ color: codeText }}>{code}</code>
      </pre>
    </Paper>
  );
};

// ─── FeatureCard ───────────────────────────────────────────────────────────

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({
  icon,
  title,
  description,
}) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid rgba(0, 207, 243, 0.2)`,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0, 207, 243, 0.15)',
        },
      }}
    >
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ color: theme.palette.mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText, mb: 3, '& svg': { fontSize: 48 } }}>{icon}</Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.25rem' }}>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, fontSize: '0.95rem' }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// ─── Main component ────────────────────────────────────────────────────────

const DeveloperDocs: React.FC = () => {
  const [groupTab, setGroupTab] = useState(0);
  const [subTabs, setSubTabs] = useState([0, 0, 0]); // one sub-tab index per group
  const [userApiKey, setUserApiKey] = useState<string>('YOUR_API_KEY');
  const [apiKeyError, setApiKeyError] = useState(false);
  const auth = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  usePageTitle('Documentation');

  const baseURL = API_BASE;

  const handleGroupChange = (_: React.SyntheticEvent, newValue: number) => {
    setGroupTab(newValue);
  };

  const handleSubTabChange = (groupIndex: number) => (_: React.SyntheticEvent, newValue: number) => {
    setSubTabs(prev => {
      const next = [...prev];
      next[groupIndex] = newValue;
      return next;
    });
  };

  // Fetch user's API keys on component mount
  useEffect(() => {
    const fetchApiKeys = async () => {
      if (!auth?.user) return;

      try {
        const token = await auth.getToken();
        const response = await fetch(`${baseURL}/api-keys`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.keys && data.keys.length > 0) {
            const activeKey = data.keys.find((key: any) => key.is_active);
            if (activeKey) {
              setUserApiKey(`vx_${activeKey.key_suffix}`);
            }
          }
        } else {
          setApiKeyError(true);
        }
      } catch (error) {
        console.error('Failed to fetch API keys:', error);
        setApiKeyError(true);
      }
    };

    fetchApiKeys();
  }, [auth, baseURL]);

  const curlExample = `# File upload transcription
curl -X POST "${baseURL}/transcription" \\
  -H "X-LVL-KEY: ${userApiKey}" \\
  -H "Content-Type: multipart/form-data" \\
  -F "file_channel1=@audio.wav" \\
  -F "config={
    \\"lang\\": \\"fr-FR\\",
    \\"sample_rate\\": 16000,
    \\"punctuation_mode\\": \\"Generated\\"
  }"

# Get transcription result
curl -X GET "${baseURL}/transcription/{transcription_id}" \\
  -H "X-LVL-KEY: ${userApiKey}"`;

  const nodeExample = `const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

async function transcribeAudio(apiKey, audioFilePath) {
  try {
    // Upload audio file for transcription
    const formData = new FormData();
    formData.append('file_channel1', fs.createReadStream(audioFilePath));
    formData.append('config', JSON.stringify({
      lang: 'fr-FR',
      sample_rate: 16000,
      punctuation_mode: 'Generated'
    }));

    const uploadResponse = await axios.post('${baseURL}/transcription', formData, {
      headers: {
        'X-LVL-KEY': apiKey,
        ...formData.getHeaders()
      }
    });

    console.log('Transcription result:', uploadResponse.data);

    // If async transcription, poll for results
    if (uploadResponse.data.id) {
      const transcriptionId = uploadResponse.data.id;

      const resultResponse = await axios.get(\`${baseURL}/transcription/\${transcriptionId}\`, {
        headers: {
          'X-LVL-KEY': apiKey
        }
      });

      console.log('Final result:', resultResponse.data);
    }

  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
}

// Usage
transcribeAudio('${userApiKey}', 'path/to/audio.wav');`;

  const pythonExample = `import requests
import json

def transcribe_audio(api_key, audio_file_path):
    """
    Transcribe audio file using Voxist API
    """
    base_url = "${baseURL}"

    # Prepare the request
    headers = {
        'X-LVL-KEY': api_key
    }

    # Prepare form data
    with open(audio_file_path, 'rb') as f:
        files = {
            'file_channel1': f
        }
        data = {
            'config': json.dumps({
                'lang': 'fr-FR',
                'sample_rate': 16000,
                'punctuation_mode': 'Generated'
            })
        }

        try:
            # Upload for transcription
            response = requests.post(
                f'{base_url}/transcription',
                headers=headers,
                files=files,
                data=data
            )

            response.raise_for_status()
            result = response.json()

            print('Transcription result:', result)

            # If async transcription, get the result
            if 'id' in result:
                transcription_id = result['id']

                # Poll for results
                result_response = requests.get(
                    f'{base_url}/transcription/{transcription_id}',
                    headers=headers
                )

                result_response.raise_for_status()
                final_result = result_response.json()

                print('Final result:', final_result)
                return final_result

            return result

        except requests.exceptions.RequestException as e:
            print(f'Error: {e}')
            return None

# Usage example
if __name__ == "__main__":
    api_key = "${userApiKey}"
    audio_file = "path/to/audio.wav"

    result = transcribe_audio(api_key, audio_file)`;

  const websocketExample = `// Real-time WebSocket transcription
const WebSocket = require('ws');

function connectWebSocket(token, language = 'fr-FR', punctuationMode = 'Generated') {
  // Pass config as URL query parameters — no separate config message needed
  const params = new URLSearchParams({
    token,
    lang: language,
    sample_rate: '16000',
    punctuation_mode: punctuationMode
  });
  const ws = new WebSocket(\`\${getWebSocketUrl(API_BASE)}/ws?\${params}\`);

  ws.on('open', () => {
    console.log('WebSocket connected — ready to stream audio');
    // Start sending audio data immediately (PCM 16-bit, 16kHz)
  });

  ws.on('message', (data) => {
    try {
      const message = JSON.parse(data.toString());

      if (message.type === 'partial') {
        console.log('Partial:', message.text);
      } else if (message.type === 'final') {
        console.log('Final:', message.text);
      }
    } catch (error) {
      // Handle binary data or non-JSON messages
    }
  });

  ws.on('error', (error) => {
    console.error('WebSocket error:', error);
  });

  return ws;
}

// Usage
const token = "YOUR_JWT_TOKEN";
const ws = connectWebSocket(token, 'fr-FR', 'Generated');`;

  // ─── Derived colors ──────────────────────────────────────────────────────
  const isDark = theme.palette.mode === 'dark';
  const pillActiveBg = tokens.colors.primary.main;
  const pillInactiveColor = theme.palette.text.secondary;

  // ─── Group tab label styles ──────────────────────────────────────────────
  const primaryTextColor = isDark ? tokens.colors.primary.light : tokens.colors.primary.darkText;

  const groupTabSx = {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '1rem',
      minHeight: 56,
      color: theme.palette.text.secondary,
      '&:hover': {
        color: primaryTextColor,
        backgroundColor: `rgba(0,207,243,0.06)`,
      },
    },
    '& .Mui-selected': {
      color: `${primaryTextColor} !important`,
    },
    '& .MuiTabs-indicator': {
      backgroundColor: isDark ? tokens.colors.primary.main : tokens.colors.primary.darkText,
      height: 3,
      borderRadius: '3px 3px 0 0',
    },
  };

  // ─── Sub-tab pill styles ─────────────────────────────────────────────────
  const pillTabsSx = {
    minHeight: 40,
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.85rem',
      minHeight: 36,
      borderRadius: `${tokens.radius.pill}px`,
      mx: 0.5,
      px: 2,
      color: pillInactiveColor,
      '&:hover': {
        color: primaryTextColor,
        backgroundColor: `rgba(0,207,243,0.08)`,
      },
    },
    '& .Mui-selected': {
      color: `${isDark ? '#fff' : tokens.colors.neutral[800]} !important`,
      backgroundColor: pillActiveBg,
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  };

  return (
    <Fade in timeout={300}>
    <Container maxWidth="lg" sx={{ py: 6, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          mb: 8,
          py: 6,
          px: 4,
          borderRadius: `${tokens.radius.lg}px`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: isDark
            ? '0 2px 12px rgba(0,0,0,0.3)'
            : '0 2px 12px rgba(0, 102, 204, 0.06)',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            background: tokens.gradients.primary,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 3,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            letterSpacing: '-0.02em',
          }}
        >
          {t('developerDocs')}
        </Typography>
        <Typography
          variant="h5"
          sx={{ mb: 6, fontWeight: 500, color: theme.palette.text.secondary, fontSize: { xs: '1.2rem', md: '1.5rem' }, lineHeight: 1.4 }}
        >
          {t('developerDocsSubtitle')}
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<GitHubIcon />}
            href="https://github.com/Voxist/asr-websocket-samples"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: tokens.gradients.horizontal,
              borderRadius: `${tokens.radius.pill}px`,
              '&:hover': {
                background: tokens.gradients.primaryHover,
              },
            }}
          >
            {t('websocketSamples')}
            <LaunchIcon sx={{ ml: 1, fontSize: 16 }} />
          </Button>
          <Button
            variant="outlined"
            startIcon={<ApiIcon />}
            sx={{
              borderColor: primaryTextColor,
              color: primaryTextColor,
              borderRadius: `${tokens.radius.pill}px`,
              '&:hover': {
                borderColor: isDark ? tokens.colors.primary.main : tokens.colors.primary.dark,
                backgroundColor: `rgba(0, 207, 243, 0.08)`,
              },
            }}
          >
            {t('apiReference')}
          </Button>
        </Stack>
      </Box>

      {/* Features Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={<SpeedIcon sx={{ fontSize: 40 }} />}
            title={t('realtimeProcessing')}
            description={t('realtimeDescription')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={<LanguageIcon sx={{ fontSize: 40 }} />}
            title={t('multiLanguageSupport')}
            description={t('multiLanguageDescription')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={<SecurityIcon sx={{ fontSize: 40 }} />}
            title={t('secureReliable')}
            description={t('secureReliableDescription')}
          />
        </Grid>
      </Grid>

      {/* M28: API key fetch failure warning */}
      {apiKeyError && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: `${tokens.radius.md}px` }}>
          Could not load your API key. Examples show placeholder values.
        </Alert>
      )}

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: `${tokens.radius.lg}px`,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${isDark ? tokens.colors.neutral[700] : tokens.colors.neutral[200]}`,
          boxShadow: isDark
            ? '0 4px 20px rgba(0,0,0,0.3)'
            : '0 4px 20px rgba(0, 102, 204, 0.08)',
        }}
      >
        {/* M27: Group-level tabs */}
        <Box sx={{ borderBottom: 1, borderColor: isDark ? tokens.colors.neutral[700] : tokens.colors.neutral[200] }}>
          <Tabs
            value={groupTab}
            onChange={handleGroupChange}
            aria-label="Documentation category tabs"
            sx={groupTabSx}
          >
            {TAB_GROUPS.map((group, i) => (
              <Tab key={group.label} id={`docs-group-tab-${i}`} aria-controls={`docs-group-panel-${i}`} label={group.label} />
            ))}
          </Tabs>
        </Box>

        {/* M27: Sub-tab pills per group */}
        <Box
          sx={{
            px: 3,
            pt: 2,
            pb: 1,
            backgroundColor: isDark
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,207,243,0.03)',
            borderBottom: `1px solid ${isDark ? tokens.colors.neutral[700] : tokens.colors.neutral[200]}`,
          }}
        >
          <Tabs
            value={subTabs[groupTab]}
            onChange={handleSubTabChange(groupTab)}
            aria-label="Documentation sub-tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={pillTabsSx}
          >
            {TAB_GROUPS[groupTab].tabs.map((tab, i) => (
              <Tab key={tab.label} id={`docs-sub-tab-${groupTab}-${i}`} aria-controls={`docs-sub-panel-${groupTab}-${i}`} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        {/* ── Group 0: Getting Started ─────────────────────────────────── */}
        {groupTab === 0 && subTabs[0] === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, fontSize: '2.25rem' }}>
              {t('quickStartGuide')}
            </Typography>

            <Alert
              severity="info"
              sx={{
                mb: 6,
                borderRadius: `${tokens.radius.md}px`,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>{t('getStartedMinutes')}</AlertTitle>
              <Typography>
                {t('followStepsIntegrate')}
              </Typography>
            </Alert>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                  <SecurityIcon sx={{ mr: 2, color: primaryTextColor, fontSize: 28 }} />
                  1. {t('getApiKey')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  {t('getApiKeyDescription')}
                </Typography>

                <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                  <UploadIcon sx={{ mr: 2, color: primaryTextColor, fontSize: 28 }} />
                  2. {t('uploadAudioFiles')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  {t('uploadAudioDescription')}
                </Typography>

                <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                  <CodeIcon sx={{ mr: 2, color: primaryTextColor, fontSize: 28 }} />
                  3. {t('integrateRealtime')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  {t('integrateRealtimeDescription')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  {t('supportedLanguages')}
                </Typography>
                <List dense>
                  {[
                    { code: 'fr-FR', name: 'French' },
                    { code: 'fr-medicalV2-32', name: 'Medical V2' },
                    { code: 'en-US', name: 'English' },
                    { code: 'de-DE', name: 'German' },
                    { code: 'es-ES', name: 'Spanish' },
                    { code: 'it-IT', name: 'Italian' },
                    { code: 'pt-PT', name: 'Portuguese' },
                    { code: 'nl-NL', name: 'Dutch' },
                    { code: 'sv-SE', name: 'Swedish' },
                    { code: 'pl-PL', name: 'Polish' },
                    { code: 'ja-JP', name: 'Japanese' },
                    { code: 'he-IL', name: 'Hebrew' },
                    { code: 'tr-TR', name: 'Turkish' },
                  ].map((lang) => (
                    <ListItem key={lang.code} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={lang.name}
                        secondary={lang.code}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        )}

        {groupTab === 0 && subTabs[0] === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, fontSize: '2.25rem' }}>
              {t('authenticationTitle')}
            </Typography>

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('apiKeyAuthentication')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('apiKeyUsage')}
            </Typography>

            <CodeBlock
              code={`X-LVL-KEY: ${userApiKey}`}
              language="http"
            />

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('jwtTokenAuthentication')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('jwtDescription')}
            </Typography>

            <CodeBlock
              code={`curl -X POST "${baseURL}/oauth/token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "grant_type": "client_credentials",
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET"
  }'`}
              language="bash"
            />

            <Alert
              severity="warning"
              sx={{
                mt: 4,
                borderRadius: `${tokens.radius.md}px`,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>{t('securityBestPractices')}</AlertTitle>
              <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', '& li': { mb: 1, lineHeight: 1.6 } }}>
                <li>{t('securityTip1')}</li>
                <li>{t('securityTip2')}</li>
                <li>{t('securityTip3')}</li>
                <li>{t('securityTip4')}</li>
              </Box>
            </Alert>
          </Box>
        )}

        {/* ── Group 1: ASR ─────────────────────────────────────────────── */}
        {groupTab === 1 && subTabs[1] === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, fontSize: '2.25rem' }}>
              {t('curlExamplesTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('curlDescription')}
            </Typography>
            <CodeBlock code={curlExample} language="bash" />
          </Box>
        )}

        {groupTab === 1 && subTabs[1] === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, fontSize: '2.25rem' }}>
              {t('nodeJsTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('nodeJsDescription')}
            </Typography>
            <Alert
              severity="info"
              sx={{
                mb: 4,
                borderRadius: `${tokens.radius.md}px`,
                '& code': {
                  backgroundColor: theme.palette.background.default,
                  padding: '4px 8px',
                  borderRadius: `${tokens.radius.xs}px`,
                  fontFamily: tokens.typography.fontFamilyMono,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
              }}
            >
              <code>npm install axios form-data</code>
            </Alert>
            <CodeBlock code={nodeExample} language="javascript" />
          </Box>
        )}

        {groupTab === 1 && subTabs[1] === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, fontSize: '2.25rem' }}>
              {t('pythonTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('pythonDescription')}
            </Typography>
            <Alert
              severity="info"
              sx={{
                mb: 4,
                borderRadius: `${tokens.radius.md}px`,
                '& code': {
                  backgroundColor: theme.palette.background.default,
                  padding: '4px 8px',
                  borderRadius: `${tokens.radius.xs}px`,
                  fontFamily: tokens.typography.fontFamilyMono,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
              }}
            >
              <code>pip install requests</code>
            </Alert>
            <CodeBlock code={pythonExample} language="python" />
          </Box>
        )}

        {groupTab === 1 && subTabs[1] === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, fontSize: '2.25rem' }}>
              {t('websocketTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('websocketDescription')}
            </Typography>

            <Alert
              severity="success"
              sx={{
                mb: 4,
                borderRadius: `${tokens.radius.md}px`,
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  href="https://github.com/Voxist/asr-websocket-samples"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 600 }}
                >
                  {t('viewMoreExamples')}
                </Button>
              }
            >
              <AlertTitle sx={{ fontWeight: 700 }}>{t('completeWebsocketSamples')}</AlertTitle>
              <Typography>
                {t('checkGithubRepo')}
              </Typography>
            </Alert>

            <CodeBlock code={websocketExample} language="javascript" />

            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                {t('websocketProtocol')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.7,
                  '& strong': { fontWeight: 600 },
                  '& code': {
                    backgroundColor: theme.palette.background.default,
                    padding: '2px 6px',
                    borderRadius: `${tokens.radius.xs}px`,
                    fontFamily: tokens.typography.fontFamilyMono,
                  },
                }}
              >
                <strong>{t('connectionUrl')}:</strong>{' '}
                <code>{getWebSocketUrl(API_BASE)}/ws?token=YOUR_JWT_TOKEN&amp;lang=fr-FR&amp;sample_rate=16000&amp;punctuation_mode=Generated</code>
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { fontWeight: 600 } }}>
                <strong>{t('audioFormat')}:</strong> {t('audioFormatDetails')}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { fontWeight: 600 } }}>
                <strong>{t('messageTypes')}:</strong> {t('messageTypesDetails')}
              </Typography>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                {t('dictationModeTitle', 'Dictation Mode')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                {t('dictationModeDescription', 'Control how punctuation is handled during transcription by setting the punctuation_mode parameter in the WebSocket URL or in a config message.')}
              </Typography>
              <List dense sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemText
                    primary={<><code style={{ fontFamily: '"JetBrains Mono", monospace', backgroundColor: theme.palette.background.default, padding: '2px 6px', borderRadius: 4 }}>Generated</code> (default)</>}
                    secondary={t('dictationModeGenerated', 'Punctuation is automatically generated by the ASR engine based on speech pauses and context.')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={<><code style={{ fontFamily: '"JetBrains Mono", monospace', backgroundColor: theme.palette.background.default, padding: '2px 6px', borderRadius: 4 }}>Dictated</code></>}
                    secondary={t('dictationModeDictated', 'Speak punctuation aloud (e.g., "period", "comma", "question mark") and the engine will insert them as punctuation marks.')}
                  />
                </ListItem>
              </List>
              <CodeBlock
                code={`// Enable dictation mode via URL query parameters
const ws = new WebSocket(
  'wss://api-asr.voxist.com/ws?token=YOUR_TOKEN&lang=fr-FR&sample_rate=16000&punctuation_mode=Dictated'
);

// Or switch mode at runtime via config message
ws.send(JSON.stringify({
  config: { punctuation_mode: 'Dictated' }
}));`}
                language="javascript"
              />
            </Box>
          </Box>
        )}

        {/* ── Group 2: TTS ─────────────────────────────────────────────── */}
        {groupTab === 2 && subTabs[2] === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: tokens.colors.tts.main, fontSize: '2.25rem' }}>
              {t('ttsQuickStartGuide', 'TTS Quick Start Guide')}
            </Typography>

            <Alert
              severity="info"
              sx={{
                mb: 6,
                borderRadius: `${tokens.radius.md}px`,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('ttsOverviewTitle', 'Text-to-Speech API')}
              </AlertTitle>
              <Typography>
                {t('ttsOverviewDescription', 'Convert text to natural-sounding speech using custom voice clones. Supports streaming output for low-latency playback.')}
              </Typography>
            </Alert>

            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <SecurityIcon sx={{ mr: 2, color: tokens.colors.tts.light, fontSize: 28 }} />
              1. {t('getApiKey')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
              {t('ttsGetApiKeyDescription', 'Create an API key from your dashboard. Use it in the X-LVL-KEY header for all TTS requests.')}
            </Typography>

            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <LanguageIcon sx={{ mr: 2, color: tokens.colors.tts.light, fontSize: 28 }} />
              2. {t('ttsListVoices', 'List Available Voices')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
              {t('ttsListVoicesDescription', 'Retrieve all voices available to your account.')}
            </Typography>
            <CodeBlock
              code={`curl -X GET "${baseURL}/v1/tts/voices" \\
  -H "X-LVL-KEY: ${userApiKey}"`}
              language="bash"
            />

            <Typography variant="h5" sx={{ mb: 3, mt: 4, display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <SpeedIcon sx={{ mr: 2, color: tokens.colors.tts.light, fontSize: 28 }} />
              3. {t('ttsSynthesize', 'Synthesize Speech')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
              {t('ttsSynthesizeDescription', 'Send a POST request with your text and receive binary audio in return.')}
            </Typography>
            <CodeBlock
              code={`curl -X POST "${baseURL}/v1/tts/VOICE_ID" \\
  -H "X-LVL-KEY: ${userApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Hello world",
    "format": "wav"
  }' \\
  --output output.wav`}
              language="bash"
            />

            <Alert
              severity="info"
              sx={{
                mt: 4,
                mb: 4,
                borderRadius: `${tokens.radius.md}px`,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('ttsSupportedFormats', 'Supported Audio Formats')}
              </AlertTitle>
              <Typography>
                {t('ttsSupportedFormatsDescription', 'wav, pcm_f32le, pcm_s16le')}
              </Typography>
            </Alert>

            <Button
              variant="contained"
              href="/voices"
              sx={{
                mt: 2,
                background: tokens.gradients.tts,
                borderRadius: `${tokens.radius.pill}px`,
                '&:hover': {
                  background: tokens.gradients.dark,
                },
              }}
            >
              {t('ttsVoiceGallery', 'Browse Voice Gallery')}
            </Button>
          </Box>
        )}

        {groupTab === 2 && subTabs[2] === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: tokens.colors.tts.main, fontSize: '2.25rem' }}>
              {t('ttsRestApiTitle', 'TTS REST API Reference')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 6, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('ttsRestApiDescription', 'Full reference for all Text-to-Speech REST endpoints.')}
            </Typography>

            {/* Synthesize */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              POST /v1/tts/:voiceId — {t('ttsSynthesize', 'Synthesize Speech')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
              {t('ttsSynthesizeDetails', 'Returns binary audio. Optional parameters: format (wav|pcm_f32le|pcm_s16le), cfg_coef (0–10), temperature (0–2), top_k (1–1000), max_duration_s (1–300). Response headers: X-Request-Id, X-Audio-Duration-S.')}
            </Typography>
            <CodeBlock
              code={`curl -X POST "${baseURL}/v1/tts/VOICE_ID" \\
  -H "X-LVL-KEY: ${userApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Hello, this is a test.",
    "format": "wav"
  }' \\
  --output output.wav`}
              language="bash"
            />

            <Divider sx={{ my: 4 }} />

            {/* List Voices */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              GET /v1/tts/voices — {t('ttsListVoices', 'List Available Voices')}
            </Typography>
            <CodeBlock
              code={`curl -X GET "${baseURL}/v1/tts/voices" \\
  -H "X-LVL-KEY: ${userApiKey}"

# Response:
# {
#   "voices": [
#     {
#       "voice_id": "abc123",
#       "name": "My Voice",
#       "description": "Custom voice clone",
#       "labels": {},
#       "preview_url": "https://...",
#       "created_at": "2024-01-01T00:00:00Z"
#     }
#   ]
# }`}
              language="bash"
            />

            <Divider sx={{ my: 4 }} />

            {/* Get Voice */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              GET /v1/tts/voices/:voiceId — {t('ttsGetVoice', 'Get Voice')}
            </Typography>
            <CodeBlock
              code={`curl -X GET "${baseURL}/v1/tts/voices/VOICE_ID" \\
  -H "X-LVL-KEY: ${userApiKey}"`}
              language="bash"
            />

            <Divider sx={{ my: 4 }} />

            {/* Clone Voice */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              POST /v1/tts/voices — {t('ttsCloneVoice', 'Clone Voice')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
              {t('ttsCloneVoiceDescription', 'Upload one or more audio samples to create a voice clone.')}
            </Typography>
            <CodeBlock
              code={`curl -X POST "${baseURL}/v1/tts/voices" \\
  -H "X-LVL-KEY: ${userApiKey}" \\
  -F "name=My Voice" \\
  -F "description=Custom voice clone" \\
  -F "files=@sample1.wav" \\
  -F "files=@sample2.wav"`}
              language="bash"
            />

            <Divider sx={{ my: 4 }} />

            {/* Delete Voice */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              DELETE /v1/tts/voices/:voiceId — {t('ttsDeleteVoice', 'Delete Voice')}
            </Typography>
            <CodeBlock
              code={`curl -X DELETE "${baseURL}/v1/tts/voices/VOICE_ID" \\
  -H "X-LVL-KEY: ${userApiKey}"`}
              language="bash"
            />

            <Divider sx={{ my: 4 }} />

            {/* Python example */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              {t('ttsPythonExample', 'Python Example')}
            </Typography>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: `${tokens.radius.md}px`,
                '& code': {
                  backgroundColor: theme.palette.background.default,
                  padding: '4px 8px',
                  borderRadius: `${tokens.radius.xs}px`,
                  fontFamily: tokens.typography.fontFamilyMono,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
              }}
            >
              <code>pip install requests</code>
            </Alert>
            <CodeBlock
              code={`import requests

def synthesize_speech(api_key, voice_id, text):
    response = requests.post(
        f"${baseURL}/v1/tts/{voice_id}",
        headers={"X-LVL-KEY": api_key, "Content-Type": "application/json"},
        json={"text": text, "format": "wav"}
    )
    response.raise_for_status()
    with open("output.wav", "wb") as f:
        f.write(response.content)
    print(f"Audio saved, duration: {response.headers.get('X-Audio-Duration-S')}s")

# Usage
synthesize_speech("${userApiKey}", "VOICE_ID", "Hello, world!")`}
              language="python"
            />

            <Divider sx={{ my: 4 }} />

            {/* Node.js example */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: tokens.colors.tts.main }}>
              {t('ttsNodeExample', 'Node.js Example')}
            </Typography>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: `${tokens.radius.md}px`,
                '& code': {
                  backgroundColor: theme.palette.background.default,
                  padding: '4px 8px',
                  borderRadius: `${tokens.radius.xs}px`,
                  fontFamily: tokens.typography.fontFamilyMono,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
              }}
            >
              <code>npm install axios fs</code>
            </Alert>
            <CodeBlock
              code={`const axios = require('axios');
const fs = require('fs');

async function synthesizeSpeech(apiKey, voiceId, text) {
  const response = await axios.post(
    \`${baseURL}/v1/tts/\${voiceId}\`,
    { text, format: 'wav' },
    {
      headers: { 'X-LVL-KEY': apiKey, 'Content-Type': 'application/json' },
      responseType: 'arraybuffer',
    }
  );

  fs.writeFileSync('output.wav', Buffer.from(response.data));
  console.log(\`Audio saved, duration: \${response.headers['x-audio-duration-s']}s\`);
}

// Usage
synthesizeSpeech('${userApiKey}', 'VOICE_ID', 'Hello, world!');`}
              language="javascript"
            />
          </Box>
        )}

        {groupTab === 2 && subTabs[2] === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: tokens.colors.tts.main, fontSize: '2.25rem' }}>
              {t('ttsWebSocketTitle', 'TTS WebSocket Streaming')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('ttsWebSocketDescription', 'Stream synthesized audio in real time using WebSocket. Audio frames are delivered as binary messages as they are generated.')}
            </Typography>

            <Alert
              severity="info"
              sx={{
                mb: 4,
                borderRadius: `${tokens.radius.md}px`,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('ttsWsConnectionTitle', 'Connection URL')}
              </AlertTitle>
              <Typography sx={{ fontFamily: tokens.typography.fontFamilyMono, fontSize: '0.9rem' }}>
                {`${getWebSocketUrl(baseURL)}/v1/tts/VOICE_ID/stream/text?token=JWT`}
              </Typography>
              <Typography sx={{ mt: 1, fontFamily: tokens.typography.fontFamilyMono, fontSize: '0.9rem' }}>
                {`${getWebSocketUrl(baseURL)}/v1/tts/VOICE_ID/stream/text?api_key=API_KEY`}
              </Typography>
            </Alert>

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              {t('ttsWsProtocol', 'Protocol')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { fontWeight: 600 } }}>
              <strong>{t('ttsWsAuth', 'Authentication')}:</strong>{' '}
              {t('ttsWsAuthDescription', 'Pass token (JWT) or api_key as query parameters during the HTTP upgrade handshake.')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { fontWeight: 600 } }}>
              <strong>{t('ttsWsClientMessages', 'Client sends')}:</strong>{' '}
              {t('ttsWsClientMessagesDescription', 'JSON object — { "text": "..." }')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { fontWeight: 600 } }}>
              <strong>{t('ttsWsServerMessages', 'Server sends')}:</strong>{' '}
              {t('ttsWsServerMessagesDescription', 'Binary audio frames while synthesizing, then a JSON control message { "type": "done" } when finished.')}
            </Typography>

            <CodeBlock
              code={`const ws = new WebSocket(
  \`${getWebSocketUrl(baseURL)}/v1/tts/VOICE_ID/stream/text?token=YOUR_JWT\`
);

ws.onopen = () => {
  ws.send(JSON.stringify({ text: "Hello, world!" }));
};

ws.onmessage = (event) => {
  if (event.data instanceof Blob) {
    // Binary audio chunk - queue for playback
    const audioChunk = event.data;
    playAudioChunk(audioChunk);
  } else {
    const msg = JSON.parse(event.data);
    if (msg.type === 'done') {
      console.log('Synthesis complete');
    }
  }
};

ws.onerror = (error) => {
  console.error('WebSocket error:', error);
};`}
              language="javascript"
            />

            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                {t('ttsWsCloseCodes', 'Close Codes')}
              </Typography>
              <Alert
                severity="warning"
                sx={{
                  borderRadius: `${tokens.radius.md}px`,
                }}
              >
                <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', '& li': { mb: 1, lineHeight: 1.7 } }}>
                  <li><strong>4401</strong> — {t('ttsWsClose4401', 'Authentication failed (invalid or missing token/api_key)')}</li>
                  <li><strong>4402</strong> — {t('ttsWsClose4402', 'Insufficient credits')}</li>
                </Box>
              </Alert>
            </Box>
          </Box>
        )}

      </Paper>

      {/* Footer */}
      <Box
        sx={{
          textAlign: 'center',
          mt: 8,
          pt: 6,
          borderTop: `2px solid ${isDark ? tokens.colors.neutral[700] : tokens.colors.neutral[200]}`,
        }}
      >
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 1.6 }}>
          {t('needHelp')}{' '}
          <Button
            color="primary"
            href="https://github.com/Voxist/asr-websocket-samples"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            {t('githubSamples')}
          </Button>{' '}
          {t('forMoreExamples')}
        </Typography>
      </Box>
    </Container>
    </Fade>
  );
};

export default DeveloperDocs;
